import React, { useState } from 'react';

interface EcommercePayload { titulos?: string[]; descricao?: string; bullets?: string[]; }
interface SocialIdea { hook?: string; corpo?: string; cta?: string; hashtags?: string[]; }

// Hook para feedback de cópia com múltiplos itens
function useCopyFeedback(){
  const [copiedId, setCopiedId] = useState<string|null>(null);
  const copy = async (text:string, id:string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(()=>setCopiedId(null), 2000);
  };
  return { copiedId, copy };
}

// Componente de botão de copiar reutilizável
function CopyButton({text, id, copiedId, copy, label, compact}: {text:string; id:string; copiedId:string|null; copy:(text:string, id:string)=>void; label?:string; compact?:boolean}){
  const isCopied = copiedId === id;

  if(compact){
    return (
      <button
        onClick={()=>copy(text, id)}
        className={`btn btn-ghost h-7 px-2 text-[10px] transition-all ${isCopied ? 'opacity-100 text-green-400' : 'opacity-40 group-hover:opacity-100'}`}
        title={isCopied ? "Copiado!" : "Copiar"}
      >
        <i className={`fa-solid ${isCopied ? 'fa-check' : 'fa-copy'}`}></i>
      </button>
    );
  }

  return (
    <button
      onClick={()=>copy(text, id)}
      className={`btn btn-ghost h-8 px-3 text-xs transition-all ${isCopied ? 'text-green-400' : ''}`}
      title={isCopied ? "Copiado!" : label || "Copiar"}
    >
      <i className={`fa-solid ${isCopied ? 'fa-check' : 'fa-copy'}`}></i>
      {label && <span>{isCopied ? 'Copiado!' : label}</span>}
    </button>
  );
}

interface ResultViewerProps {
  raw: any; // resposta vinda do backend (pode ter .text ou já JSON)
  specialist: 'none' | 'ecommerce' | 'social';
}

// Tenta interpretar texto JSON em objeto
function safeParse(text:string | undefined){
  if(!text) return null; try { return JSON.parse(text); } catch { return null; }
}

// Parser inteligente de texto estruturado
function parseStructuredText(text: string) {
  const lines = text.split('\n');
  const sections: Array<{type: 'section'|'numbered'|'bullets'|'paragraph'|'separator', title?: string, items?: string[], content?: string}> = [];

  let currentSection: string | null = null;
  let currentItems: string[] = [];
  let currentParagraph: string[] = [];

  const flushParagraph = () => {
    if(currentParagraph.length > 0){
      sections.push({type: 'paragraph', content: currentParagraph.join('\n')});
      currentParagraph = [];
    }
  };

  const flushItems = () => {
    if(currentItems.length > 0 && currentSection){
      const isNumbered = currentItems[0]?.match(/^\d+\./);
      sections.push({
        type: isNumbered ? 'numbered' : 'bullets',
        title: currentSection,
        items: currentItems.map(item => item.replace(/^(\d+\.|\•|\-|\*)\s*/, '').trim())
      });
      currentItems = [];
      currentSection = null;
    }
  };

  lines.forEach(line => {
    const trimmed = line.trim();

    // Separador
    if(trimmed.match(/^(---|===|___)/)){
      flushParagraph();
      flushItems();
      sections.push({type: 'separator'});
      return;
    }

    // Seção (CAPS seguido de :)
    if(trimmed.match(/^[A-ZÁÉÍÓÚ\s]+:$/)){
      flushParagraph();
      flushItems();
      currentSection = trimmed.replace(':', '');
      return;
    }

    // Item numerado (1., 2., etc)
    if(trimmed.match(/^\d+\.\s+/)){
      flushParagraph();
      currentItems.push(trimmed);
      return;
    }

    // Bullet point (•, -, *)
    if(trimmed.match(/^[•\-\*]\s+/)){
      flushParagraph();
      currentItems.push(trimmed);
      return;
    }

    // Linha vazia
    if(!trimmed){
      flushParagraph();
      flushItems();
      return;
    }

    // Texto normal - acumula em parágrafo
    currentParagraph.push(trimmed);
  });

  // Flush final
  flushParagraph();
  flushItems();

  return sections;
}

export const ResultViewer: React.FC<ResultViewerProps> = ({ raw, specialist }) => {
  const { copiedId, copy } = useCopyFeedback();

  if(!raw) return null;

  // TODOS os especialistas usam o parser inteligente
  const text: string | undefined = raw.text || (typeof raw === 'string' ? raw : undefined);
  let displayText = text || '';

  // Se vier como objeto/array, extrai texto
  if(!displayText && raw){
    if(Array.isArray(raw)){
      displayText = raw.map(item => {
        if(typeof item === 'string') return item;
        if(typeof item === 'object' && item.text) return item.text;
        return JSON.stringify(item, null, 2);
      }).join('\n\n---\n\n');
    } else if(typeof raw === 'object'){
      displayText = raw.content || raw.message || raw.output || JSON.stringify(raw, null, 2);
    } else {
      displayText = String(raw);
    }
  }

  const sections = parseStructuredText(displayText);

  // Detecta se é formato de social media (com Hook:, Corpo:, etc)
  const isSocialFormat = displayText.toLowerCase().includes('hook:') || displayText.toLowerCase().includes('ideia');

  if(isSocialFormat){
    return <SocialMediaResult text={displayText} copiedId={copiedId} copy={copy} />;
  }

  // Conta quantos itens temos
  const totalItems = sections.reduce((acc, s) => {
    if(s.type === 'numbered' || s.type === 'bullets') return acc + (s.items?.length || 0);
    if(s.type === 'paragraph') return acc + 1;
    return acc;
  }, 0);

  return (
    <div className="space-y-5">
      {/* Header com info e botão de copiar tudo */}
      {totalItems > 0 && (
        <div className="panel p-4 flex items-center justify-between bg-gradient-to-r from-slate-900/40 to-slate-900/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-500/10 flex items-center justify-center">
              <i className="fa-solid fa-wand-magic-sparkles" style={{color:'var(--cg-accent)'}}></i>
            </div>
            <div>
              <div className="text-sm font-semibold">Conteúdo gerado com sucesso</div>
              <div className="text-xs opacity-70">{totalItems} {totalItems === 1 ? 'item gerado' : 'itens gerados'} • Clique <i className="fa-solid fa-copy text-[9px] mx-0.5"></i> em cada item para copiar individualmente</div>
            </div>
          </div>
          <CopyButton
            text={displayText}
            id="all-top"
            copiedId={copiedId}
            copy={copy}
            label="Copiar Tudo"
          />
        </div>
      )}

      {sections.map((section, idx) => {
        // Seção com título e lista numerada (ex: TÍTULOS:)
        if(section.type === 'numbered' && section.items){
          return (
            <div key={idx} className="panel p-6">
              <div className="flex items-center justify-between mb-5 pb-3 border-b border-[var(--cg-border)]">
                <h3 className="text-lg font-bold flex items-center gap-2 heading">
                  <i className="fa-solid fa-list-ol" style={{color:'var(--cg-accent)'}}></i>
                  {section.title}
                </h3>
                <span className="badge text-xs">{section.items.length} opções</span>
              </div>
              <div className="space-y-4">
                {section.items.map((item, i) => (
                  <div key={i} className="panel-alt p-5 group hover:shadow-md transition-all rounded-xl">
                    <div className="flex justify-between items-start gap-4 mb-3">
                      <div className="text-[10px] uppercase tracking-wider opacity-60 font-bold">Opção {i+1}</div>
                      <CopyButton text={item} id={`item-${idx}-${i}`} copiedId={copiedId} copy={copy} compact />
                    </div>
                    <p className="text-base leading-relaxed pr-8">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        }

        // Lista com bullets (ex: BENEFÍCIOS:)
        if(section.type === 'bullets' && section.items){
          return (
            <div key={idx} className="panel p-6">
              <div className="flex items-center justify-between mb-5 pb-3 border-b border-[var(--cg-border)]">
                <h3 className="text-lg font-bold flex items-center gap-2 heading">
                  <i className="fa-solid fa-list-check" style={{color:'var(--cg-accent)'}}></i>
                  {section.title}
                </h3>
                <span className="badge text-xs">{section.items.length} itens</span>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {section.items.map((item, i) => (
                  <div key={i} className="panel-alt p-5 group hover:shadow-sm transition-all rounded-xl">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <i className="fa-solid fa-circle-check mt-0.5 flex-shrink-0 text-base" style={{color:'var(--cg-accent)'}}></i>
                      <CopyButton text={item} id={`bullet-${idx}-${i}`} copiedId={copiedId} copy={copy} compact />
                    </div>
                    <p className="text-sm leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        }

        // Parágrafo (ex: DESCRIÇÃO:)
        if(section.type === 'paragraph' && section.content){
          return (
            <div key={idx} className="panel p-6">
              <div className="flex justify-between items-center mb-4 pb-3 border-b border-[var(--cg-border)]">
                <h3 className="text-lg font-bold heading">Descrição</h3>
                <CopyButton text={section.content} id={`para-${idx}`} copiedId={copiedId} copy={copy} label="Copiar" />
              </div>
              <div className="panel-alt p-5 text-base leading-relaxed whitespace-pre-wrap rounded-xl">
                {section.content}
              </div>
            </div>
          );
        }

        // Separador
        if(section.type === 'separator'){
          return <div key={idx} className="divider my-4" />;
        }

        return null;
      })}
    </div>
  );
};

// Componente especializado para resultados de Social Media
function SocialMediaResult({text, copiedId, copy}: {text:string; copiedId:string|null; copy:(text:string, id:string)=>void}){
  // Parser específico para formato de social media
  const ideas = text.split(/---\s*IDEIA\s*\d+\s*---|---\s*Ideia\s*\d+\s*---/).filter(Boolean);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="panel p-4 flex items-center justify-between bg-gradient-to-r from-slate-900/40 to-slate-900/60">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-brand-500/10 flex items-center justify-center">
            <i className="fa-solid fa-hashtag" style={{color:'var(--cg-accent)'}}></i>
          </div>
          <div>
            <div className="text-sm font-semibold">{ideas.length} {ideas.length === 1 ? 'ideia gerada' : 'ideias geradas'}</div>
            <div className="text-xs opacity-70">Clique <i className="fa-solid fa-copy text-[9px] mx-0.5"></i> em cada seção para copiar individualmente, ou use "Copiar Tudo" para copiar a ideia completa</div>
          </div>
        </div>
        <CopyButton
          text={text}
          id="all-ideas-top"
          copiedId={copiedId}
          copy={copy}
          label="Copiar Tudo"
        />
      </div>

      {ideas.map((ideaText, ideaIdx) => {
        const lines = ideaText.trim().split('\n');
        let hook = '', corpo = '', cta = '', hashtags = '';

        lines.forEach(line => {
          const lower = line.toLowerCase();
          if(lower.startsWith('hook:')) hook = line.substring(line.indexOf(':')+1).trim();
          else if(lower.startsWith('corpo:')) corpo = line.substring(line.indexOf(':')+1).trim();
          else if(lower.startsWith('cta:')) cta = line.substring(line.indexOf(':')+1).trim();
          else if(lower.startsWith('hashtags:')) hashtags = line.substring(line.indexOf(':')+1).trim();
        });

        const fullIdea = [hook, corpo, cta, hashtags].filter(Boolean).join('\n\n');

        return (
          <div key={ideaIdx} className="panel p-6 space-y-5">
            <div className="flex items-start justify-between gap-3">
              <div className="tag">Ideia {ideaIdx + 1}</div>
              <CopyButton
                text={fullIdea}
                id={`idea-${ideaIdx}-full`}
                copiedId={copiedId}
                copy={copy}
                label="Copiar Tudo"
              />
            </div>

            {hook && (
              <div className="panel-alt p-4 space-y-2 group">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider opacity-60 font-semibold flex items-center gap-2">
                    <i className="fa-solid fa-bolt" style={{color:'var(--cg-accent)'}}></i>
                    Hook
                  </div>
                  <CopyButton text={hook} id={`hook-${ideaIdx}`} copiedId={copiedId} copy={copy} compact />
                </div>
                <p className="font-semibold text-base leading-snug">{hook}</p>
              </div>
            )}

            {corpo && (
              <div className="panel-alt p-4 space-y-2 group">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider opacity-60 font-semibold flex items-center gap-2">
                    <i className="fa-solid fa-align-left" style={{color:'var(--cg-accent)'}}></i>
                    Corpo
                  </div>
                  <CopyButton text={corpo} id={`corpo-${ideaIdx}`} copiedId={copiedId} copy={copy} compact />
                </div>
                <p className="text-sm leading-relaxed">{corpo}</p>
              </div>
            )}

            {(cta || hashtags) && (
              <div className="grid md:grid-cols-2 gap-3">
                {cta && (
                  <div className="panel-alt p-4 space-y-2 group">
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-wider opacity-60 font-semibold flex items-center gap-2">
                        <i className="fa-solid fa-hand-pointer" style={{color:'var(--cg-accent)'}}></i>
                        CTA
                      </div>
                      <CopyButton text={cta} id={`cta-${ideaIdx}`} copiedId={copiedId} copy={copy} compact />
                    </div>
                    <p className="text-xs font-medium" style={{color:'var(--cg-accent)'}}>{cta}</p>
                  </div>
                )}

                {hashtags && (
                  <div className="panel-alt p-4 space-y-2 group">
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-wider opacity-60 font-semibold flex items-center gap-2">
                        <i className="fa-solid fa-hashtag" style={{color:'var(--cg-accent)'}}></i>
                        Hashtags
                      </div>
                      <CopyButton text={hashtags} id={`tags-${ideaIdx}`} copiedId={copiedId} copy={copy} compact />
                    </div>
                    <p className="text-xs font-mono opacity-90">{hashtags}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

